import { IStackStyles, Stack, StackItem } from "@fluentui/react";
import React, { createContext, Dispatch, SetStateAction, useState } from "react";
import { useComponentLogger } from "../../shared/logging";
import { validateComponent } from "../../shared/validation";
import { useViewport } from "../../styles";
import { firstColumnIndex, maxBannerColumnsValidator, minBannerColumnsValidator, secondColumnIndex, thirdColumnIndex } from "./Banner.helpers";

export interface BannerProps {
    /**
     * Banner ID.
     */
    readonly id?: string;
    /**
     * If the banner is expanded it adds additional padding on top of itself.
     */
    readonly expanded?: boolean;
}

export interface BannerContextType {
    readonly setDisplaySecondColumnContainer: Dispatch<SetStateAction<boolean>>;
    readonly setDisplayThirdColumnContainer: Dispatch<SetStateAction<boolean>>;
}

export const BannerContext = createContext<BannerContextType>(
    {
        setDisplaySecondColumnContainer: () => { return; },
        setDisplayThirdColumnContainer: () => { return; }
    }
);

export const Banner: React.FC<BannerProps> = ({
    id,
    expanded = true,
    children
}) => {
    useComponentLogger("Banner", id);

    const [displaySecondColumnContainer, setDisplaySecondColumnContainer] = useState(true);
    const [displayThirdColumnContainer, setDisplayThirdColumnContainer] = useState(true);

    const columns: readonly React.ReactNode[] = React.Children.toArray(children);
    validateComponent("Banner", [minBannerColumnsValidator(columns), maxBannerColumnsValidator(columns)]);
    const numColumns = columns.length;

    const viewport = useViewport();
    const isSmallViewport = ["s", "m", "l"].includes(viewport);

    const bannerPadding = `${isSmallViewport ? "24px" : "30px"} 0 0`;
    const bannerContainerPadding = expanded ? bannerPadding : undefined;
    const defaultStackItemShrink = 0;
    const firstColumnWidth = !isSmallViewport && numColumns > 1 ? "50%" : "100%";

    const getSecondaryColumnStyles = (displayColumnContainer: boolean): IStackStyles => {
        if (displayColumnContainer) {
            return {
                root: {
                    width: viewport === "s" ? "100%" : "50%"
                }
            };
        } else {
            return {
                root: {
                    width: "0%",
                    overflow: "hidden"
                }
            };
        }
    };

    const secondaryStackProps = {
        horizontalAlign: isSmallViewport ? "start" as const : "end" as const
    };
    const secondaryStackWidth = isSmallViewport ? "100%" : "50%";

    return (
        <BannerContext.Provider value={{ setDisplaySecondColumnContainer, setDisplayThirdColumnContainer }}>
            <Stack
                horizontal={true}
                wrap={true}
                styles={{ root: { width: "100%", padding: bannerContainerPadding } }}
                id={id}
            >
                <StackItem styles={{ root: { width: firstColumnWidth } }} shrink={defaultStackItemShrink}>
                    {columns[firstColumnIndex]}
                </StackItem>
                {numColumns > 1 && (
                    <Stack
                        {...secondaryStackProps}
                        horizontal={true}
                        wrap={true}
                        styles={{ root: { width: secondaryStackWidth } }}
                    >
                        {columns[secondColumnIndex] && (
                            <StackItem styles={getSecondaryColumnStyles(displaySecondColumnContainer)} shrink={defaultStackItemShrink} >
                                {columns[secondColumnIndex]}
                            </StackItem>
                        )}
                        {columns[thirdColumnIndex] && (
                            <StackItem styles={getSecondaryColumnStyles(displayThirdColumnContainer)} shrink={defaultStackItemShrink} >
                                {columns[thirdColumnIndex]}
                            </StackItem>
                        )}
                    </Stack>
                )}
            </Stack>
        </BannerContext.Provider>
    );
};
